import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Edit, Save, Cancel } from '@mui/icons-material';
import MealPlansContext from '../../context/meal-plans-context';
import ShoppingListContext from '../../context/shopping-list-context';
import Loader from '../../components/UI/General/Loader';
import PlanCompletion from '../../components/MealPlan/PlanCompletion';
import SuggestionWizard from '../../components/MealPlan/SuggestionWizard';

const MealPlanDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { plans, isLoading, updatePlan, removeMealFromPlan } = useContext(MealPlansContext);
  const { generateFromPlan } = useContext(ShoppingListContext);

  const [completionOpen, setCompletionOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const plan = plans.find((p) => p.id === id);

  if (isLoading) {
    return <Loader />;
  }

  if (!plan) {
    return (
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <Alert severity="error">Plán nebyl nalezen</Alert>
      </Container>
    );
  }

  const isOverdue = plan.status === 'active' && plan.dueDate !== null && plan.dueDate < new Date();

  const handleStartEdit = () => {
    setEditName(plan.name);
    setEditDate(plan.dueDate ? plan.dueDate.toISOString().slice(0, 10) : '');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError(null);
    try {
      await updatePlan(plan.id, editName, editDate || null);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    setError(null);
    try {
      await removeMealFromPlan(itemId);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleGenerateShoppingList = async () => {
    setError(null);
    try {
      await generateFromPlan(plan.id, 'add');
      setSuccess('Nákupní seznam byl vygenerován');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleCompletionDone = () => {
    setCompletionOpen(false);
    navigate('/meal-plans');
  };

  const handleWizardComplete = () => {
    setWizardOpen(false);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Header */}
      {editing ? (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Název plánu"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Termín (volitelné)"
            type="date"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<Save />}
              variant="contained"
              onClick={handleSaveEdit}
              disabled={saving || !editName.trim()}
              size="small"
            >
              Uložit
            </Button>
            <Button startIcon={<Cancel />} onClick={() => setEditing(false)} disabled={saving} size="small">
              Zrušit
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5">{plan.name}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {plan.dueDate ? `Termín: ${plan.dueDate.toLocaleDateString('cs-CZ')}` : 'Bez termínu'}
              </Typography>
              <Chip
                label={plan.status === 'active' ? 'Aktivní' : 'Dokončený'}
                size="small"
                color={plan.status === 'active' ? 'primary' : 'default'}
              />
              {isOverdue && <Chip label="Po termínu" size="small" color="error" />}
            </Stack>
          </Box>
          {plan.status === 'active' && (
            <IconButton onClick={handleStartEdit} size="small">
              <Edit />
            </IconButton>
          )}
        </Stack>
      )}

      {/* Action buttons */}
      {plan.status === 'active' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          <Button variant="outlined" onClick={() => navigate('/your-meals')} size="small">
            Přidat jídlo
          </Button>
          <Button variant="outlined" onClick={() => setWizardOpen(true)} size="small">
            Nechat navrhnout
          </Button>
          <Button variant="outlined" onClick={handleGenerateShoppingList} size="small">
            Nákupní seznam
          </Button>
          <Button
            variant="contained"
            onClick={() => setCompletionOpen(true)}
            size="small"
            disabled={plan.items.length === 0}
          >
            Dokončit plán
          </Button>
        </Box>
      )}

      {/* Meals list */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Jídla v plánu ({plan.items.length})
      </Typography>

      {plan.items.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          Plán zatím neobsahuje žádná jídla
        </Typography>
      )}

      <Grid container spacing={2}>
        {plan.items.map((item) => {
          const itemImage = item.meal.imageThumbnail || item.meal.image || '/images/meal-placeholder.png';
          return (
            <Grid item xs={12} sm={6} key={item.id}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <img
                        alt={item.meal.name}
                        src={itemImage}
                        style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                      />
                      <Typography variant="body1">{item.meal.name}</Typography>
                    </Stack>
                    {plan.status === 'active' && (
                      <IconButton
                        onClick={() => handleRemoveItem(item.id)}
                        size="small"
                        color="error"
                        aria-label="Odebrat z plánu"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dialogs */}
      <PlanCompletion
        plan={plan}
        open={completionOpen}
        onClose={() => setCompletionOpen(false)}
        onComplete={handleCompletionDone}
      />
      <SuggestionWizard
        planId={plan.id}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={handleWizardComplete}
      />
    </Container>
  );
};

export default MealPlanDetailPage;
